import { ChangeDetectionStrategy, Component, Injectable, Input, OnInit, signal } from '@angular/core';
import { RestTimerService } from 'services/rest-timer.service';

export enum RestTimerDemoState {
  Running = 'running',
  Expired = 'expired',
}

// Stand-in for RestTimerService, used only on the Test UI Components page (#6020).
// The rest-timer pill and its expanded view read the app's single RestTimerService and draw
// nothing unless a real timer runs. Starting a real one from a test page would take the screen
// wake lock and schedule an OS notification for a workout that does not exist, and on web it
// refuses to start unless a debug setting is on. This stand-in has the same signals and methods
// the two components read, and changes only its own values.
@Injectable()
export class DemoRestTimerService {
  readonly remainingSeconds = signal<number | null>(null);
  readonly isRunning = signal<boolean>(false);
  readonly exerciseName = signal<string | null>(null);
  readonly isExpired = signal<boolean>(false);

  // the real service refuses on web unless a debug setting is on; the stand-in always allows
  isAllowedOnThisPlatform(): boolean {
    return true;
  }

  // the expanded view's -15 s / +15 s buttons; never goes below zero
  async adjust(deltaSeconds: number): Promise<void> {
    this.remainingSeconds.update(seconds => Math.max(0, (seconds ?? 0) + deltaSeconds));
  }

  // the Skip button: hides the timer, as the real one does when a rest is skipped
  async skip(): Promise<void> {
    this.isRunning.set(false);
    this.isExpired.set(false);
    this.remainingSeconds.set(null);
  }

  // shows a timer for the sample exercise "Bench Press": 1:35 left when running, 0 when expired
  show(state: RestTimerDemoState): void {
    this.exerciseName.set('Bench Press');
    this.isRunning.set(true);
    this.isExpired.set(state === RestTimerDemoState.Expired);
    this.remainingSeconds.set(state === RestTimerDemoState.Expired ? 0 : 95);
  }
}

// One rest-timer pill driven by its own stand-in service. Tapping the pill opens the expanded
// view exactly as the workout page does; Skip hides the timer, and "Restart Demo" brings it back.
@Component({
  selector: 'cs-test-rest-timer-demo',
  template: `
    <cs-rest-timer-pill (expand)="showExpanded = true"></cs-rest-timer-pill>
    @if (showExpanded) {
      <cs-rest-timer-expanded (collapse)="showExpanded = false"></cs-rest-timer-expanded>
    }
    @if (!timer.isRunning()) {
      <cs-button small secondary (click)="restart()" [label]="'Restart Demo' | untranslated"></cs-button>
    }
  `,
  providers: [
    DemoRestTimerService,
    { provide: RestTimerService, useExisting: DemoRestTimerService },
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsTestRestTimerDemoComponent implements OnInit {
  @Input() state: RestTimerDemoState = RestTimerDemoState.Running;
  showExpanded = false;

  constructor(readonly timer: DemoRestTimerService) {}

  // starts in the state the page asked for
  ngOnInit(): void {
    this.timer.show(this.state);
  }

  // brings the timer back after Skip hid it
  restart(): void {
    this.showExpanded = false;
    this.timer.show(this.state);
  }
}
